/**
 * Global settings (CLAUDE.md 12.2).
 *
 * Each row says where its effective value came from — `config.toml` or an override
 * stored here. That badge is not decoration: an operator who has just edited a managed
 * config file and seen nothing change needs to be told that the database is winning,
 * and the alternative is a support ticket.
 *
 * Secrets (the SMTP password) are never sent to the browser. The field shows "set" or
 * "not set" and an empty box; submitting it empty leaves the stored value alone, so
 * saving a retention window does not silently erase the mail password.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAdminSettings, useUpdateAdminSettings } from '@/api/admin'
import { ApiError } from '@/api/client'
import type { AdminSetting } from '@/api/types'
import { Pill } from '@/components/admin/AdminShell'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { FieldInput } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useToast } from '@/components/ui/toast'

const GROUPS = ['platform', 'reports', 'support', 'retention', 'smtp'] as const

const selectClass = 'h-11 w-full rounded-md border border-border bg-surface px-3 text-text'

export function AdminSettings() {
  const { t } = useTranslation(['admin', 'common'])
  const { toast } = useToast()

  const settings = useAdminSettings()
  const update = useUpdateAdminSettings()

  // Only touched keys are sent. A PATCH echoing every value back would rewrite rows
  // nobody edited, and turn every setting's `source` into "database" on first save.
  const [draft, setDraft] = useState<Record<string, unknown>>({})
  const dirty = Object.keys(draft).length > 0

  if (settings.isLoading) return <Skeleton className="h-96" />

  const rows = settings.data?.settings ?? []
  // A secret arrives as `null` and its box starts empty; every other kind arrives
  // with its effective value.
  const valueOf = (row: AdminSetting): unknown =>
    row.key in draft ? draft[row.key] : (row.value ?? '')

  const save = () =>
    update.mutate(draft, {
      onSuccess: () => {
        setDraft({})
        toast({ title: t('common.saved', { ns: 'common' }), variant: 'success' })
      },
      onError: (error) =>
        toast({
          title: t('common.error', { ns: 'common' }),
          description: error instanceof ApiError ? error.localised : undefined,
          variant: 'error',
        }),
    })

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold">{t('admin.settings.title')}</h2>

      {GROUPS.map((group) => {
        const groupRows = rows.filter((row) => row.group === group)
        if (groupRows.length === 0) return null

        return (
          <Card key={group}>
            <CardHeader>
              <CardTitle>{t(`admin.settings.group.${group}`)}</CardTitle>
            </CardHeader>
            <CardBody className="space-y-4">
              {groupRows.map((row) => (
                <SettingControl
                  key={row.key}
                  row={row}
                  value={valueOf(row)}
                  onChange={(value) => setDraft((current) => ({ ...current, [row.key]: value }))}
                />
              ))}
            </CardBody>
          </Card>
        )
      })}

      <div className="sticky bottom-0 flex gap-2 border-t border-border bg-bg py-3">
        <Button variant="primary" disabled={!dirty} loading={update.isPending} onClick={save}>
          {t('action.save', { ns: 'common' })}
        </Button>
        {dirty && (
          <Button onClick={() => setDraft({})}>{t('action.cancel', { ns: 'common' })}</Button>
        )}
      </div>
    </div>
  )
}

function SourceBadge({ source }: { source: AdminSetting['source'] }) {
  const { t } = useTranslation('admin')
  return (
    <Pill tone={source === 'database' ? 'warn' : 'muted'}>
      {t(`admin.settings.source.${source}`)}
    </Pill>
  )
}

function SettingControl({
  row,
  value,
  onChange,
}: {
  row: AdminSetting
  value: unknown
  onChange: (value: unknown) => void
}) {
  const { t } = useTranslation('admin')
  const label = t(`admin.settings.key.${row.key}`)
  // Not every setting needs an explanation; `defaultValue: ''` keeps a missing hint
  // from rendering as a raw key.
  const hint = t(`admin.settings.hint.${row.key}`, { defaultValue: '' })

  if (row.kind === 'bool') {
    return (
      <Field description={hint || undefined}>
        <div className="flex items-center gap-2">
          <input
            id={`setting-${row.key}`}
            type="checkbox"
            checked={Boolean(value)}
            onChange={(event) => onChange(event.target.checked)}
          />
          <label htmlFor={`setting-${row.key}`} className="text-sm">
            {label}
          </label>
          <SourceBadge source={row.source} />
        </div>
      </Field>
    )
  }

  if (row.kind === 'choice') {
    return (
      <Field description={hint || undefined}>
        <FieldLabel hint={<SourceBadge source={row.source} />}>{label}</FieldLabel>
        <select
          className={selectClass}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        >
          {row.choices.map((choice) => (
            <option key={choice} value={choice}>
              {t(`admin.settings.${row.key}.${choice}`, { defaultValue: choice })}
            </option>
          ))}
        </select>
      </Field>
    )
  }

  if (row.kind === 'int') {
    return (
      <Field description={hint || undefined}>
        <FieldLabel hint={<SourceBadge source={row.source} />}>{label}</FieldLabel>
        <FieldInput
          type="number"
          inputMode="numeric"
          min={row.minimum ?? undefined}
          max={row.maximum ?? undefined}
          value={String(value ?? '')}
          onChange={(event) => onChange(Number(event.target.value))}
        />
      </Field>
    )
  }

  if (row.secret) {
    return (
      <Field description={hint || undefined}>
        <FieldLabel
          hint={
            <span className="flex items-center gap-1">
              <Pill tone={row.is_set ? 'pass' : 'muted'}>
                {row.is_set ? t('admin.settings.secret_set') : t('admin.settings.secret_not_set')}
              </Pill>
              <SourceBadge source={row.source} />
            </span>
          }
        >
          {label}
        </FieldLabel>
        <FieldInput
          type="password"
          autoComplete="new-password"
          placeholder={t('admin.settings.secret_placeholder')}
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        />
      </Field>
    )
  }

  // A disclaimer is prose and runs to several lines; everything else is one line.
  const multiline = row.key === 'report_disclaimer'
  return (
    <Field description={hint || undefined}>
      <FieldLabel hint={<SourceBadge source={row.source} />}>{label}</FieldLabel>
      {multiline ? (
        <textarea
          className="min-h-24 w-full rounded-md border border-border bg-surface p-3 text-sm text-text"
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <FieldInput
          value={String(value ?? '')}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  )
}
